import { useCallback } from 'react';
import { useRouter } from 'expo-router';

import type { GetSharedLineItemProductForRestaurantQuery } from '../../GraphQL/ShareLineItemScreen.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useShareLineItemNavigation = (params: Params) => {
  const {
    baseProductSlug,
    lineItemSlug,
    productForRestaurant,
    selectedDeliveryAddressId,
    selectedRestaurantId,
    sharedLineItemId,
  } = params;

  const router = useRouter();

  // ─── Handlers ────────────────────────────────────────────────────────

  const navigateToLocationSearch = useCallback(() => {
    if (!lineItemSlug) return;

    router.navigate({
      pathname: '/share/[lineItemSlug]/search-location',
      params: {
        baseProductSlug,
        lineItemSlug,
        sharedLineItemId,
      },
    });
  }, [baseProductSlug, lineItemSlug, router, sharedLineItemId]);

  const navigateToCustomization = useCallback(() => {
    if (!productForRestaurant || !selectedRestaurantId || !lineItemSlug) {
      navigateToLocationSearch();

      return;
    }

    if (selectedDeliveryAddressId) {
      router.navigate({
        pathname: '/delivery/[addressId]/[productSlug]',
        params: {
          addressId: selectedDeliveryAddressId,
          productSlug: productForRestaurant.slug,
          sharedLineItemId,
        },
      });

      return;
    }

    router.navigate({
      pathname: '/[restaurantSlug]/[productSlug]',
      params: {
        productSlug: productForRestaurant.slug,
        restaurantSlug: productForRestaurant.restaurant.slug,
        sharedLineItemId,
      },
    });
  }, [
    lineItemSlug,
    navigateToLocationSearch,
    productForRestaurant,
    router,
    selectedDeliveryAddressId,
    selectedRestaurantId,
    sharedLineItemId,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    navigateToCustomization,
    navigateToLocationSearch,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductSlug: string;
  lineItemSlug: string;
  productForRestaurant?: ProductForRestaurant;
  selectedDeliveryAddressId?: string;
  selectedRestaurantId?: string;
  sharedLineItemId: string;
};

type ProductForRestaurant = NonNullable<
  GetSharedLineItemProductForRestaurantQuery['productForRestaurant']
>;
